/**
 * ItemGo Messaging — FCM configuration and token management
 */
import { getMessaging, getToken, deleteToken } from "firebase/messaging";
import { doc, setDoc, deleteDoc } from "firebase/firestore";
import { app, db } from "@itemgo/core";
let messagingInstance = null;
/**
 * Initialize Firebase Messaging.
 * Returns null if messaging is not supported (e.g. Safari private mode, iOS WKWebView).
 */
export function getMessagingInstance() {
    if (typeof window === "undefined")
        return null;
    if (messagingInstance)
        return messagingInstance;
    try {
        messagingInstance = getMessaging(app);
        return messagingInstance;
    }
    catch {
        return null;
    }
}
/**
 * Register Firebase Messaging service worker explicitly.
 * This MUST be called before getToken() on web.
 */
export async function registerMessagingServiceWorker() {
    if (typeof window === "undefined")
        return;
    if (!("serviceWorker" in navigator)) {
        console.warn("[Messaging] Service workers not supported");
        return;
    }
    try {
        const registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
            scope: "./",
        });
        console.log("[Messaging] Service worker registered:", registration.scope);
    }
    catch (err) {
        console.warn("[Messaging] Service worker registration failed:", err);
    }
}
/**
 * Request browser notification permission and get FCM token.
 * Saves token to Firestore under users/{uid}/fcmTokens/{token}.
 * Automatically registers the service worker first.
 */
export async function requestNotificationPermission(userId) {
    const messaging = getMessagingInstance();
    if (!messaging) {
        console.warn("[Messaging] FCM not supported in this environment");
        return null;
    }
    // Register service worker FIRST (required for web push)
    await registerMessagingServiceWorker();
    if (typeof window === "undefined" || !("Notification" in window)) {
        console.warn("[Messaging] Notification API not supported in this browser");
        return null;
    }
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
        console.warn("[Messaging] Notification permission denied");
        return null;
    }
    try {
        const vapidKey = import.meta.env?.VITE_FCM_VAPID_KEY || undefined;
        const currentToken = await getToken(messaging, vapidKey ? { vapidKey } : undefined);
        if (currentToken) {
            await setDoc(doc(db, "users", userId, "fcmTokens", currentToken), {
                token: currentToken,
                platform: "web",
                createdAt: new Date(),
            });
            console.log("[Messaging] FCM token saved:", currentToken);
            return currentToken;
        }
        return null;
    }
    catch (err) {
        console.error("[Messaging] Failed to get FCM token:", err);
        return null;
    }
}
/**
 * Delete the current FCM token and remove from Firestore.
 */
export async function deleteFcmToken(userId) {
    const messaging = getMessagingInstance();
    if (!messaging)
        return;
    try {
        const currentToken = await getToken(messaging);
        if (currentToken) {
            await deleteToken(messaging);
            await deleteDoc(doc(db, "users", userId, "fcmTokens", currentToken));
            console.log("[Messaging] FCM token deleted");
        }
    }
    catch (err) {
        console.error("[Messaging] Failed to delete FCM token:", err);
    }
}
//# sourceMappingURL=config.js.map